import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import ChartAxes from "./ChartAxes";
import { TYPE_LABELS, TYPE_COLORS, MONTH_LABELS } from "../constants";

function getMonthKey(date) {
    return `${date.getFullYear()}-${date.getMonth()}`;
}

export default function DashboardLeaveTrend({
    leaves,
    compact = false,
    title = "Tendance 6 mois",
    subtitle = "Demandes créées par type",
    monthsCount = 6,
    emptyLabel = "Aucune donnée récente",
}) {
    const chartWidth = compact ? 180 : 220;
    const chartHeight = 80;
    const chartTop = 8;
    const chartBottom = 12;
    const chartLeft = 12;

    const { months, series, maxCount, total } = useMemo(() => {
        const now = new Date();
        const monthKeys = [];
        const monthLabels = [];
        const safeMonthsCount = Math.max(1, Number(monthsCount) || 1);
        for (let i = safeMonthsCount - 1; i >= 0; i -= 1) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            monthKeys.push(getMonthKey(d));
            monthLabels.push(`${MONTH_LABELS[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`);
        }

        const baseCounts = monthKeys.reduce((acc, key) => {
            acc[key] = 0;
            return acc;
        }, {});
        const countsByType = {};

        (leaves || []).forEach((leave) => {
            const rawDate =
                leave.fromDate ??
                leave.from ??
                leave.created_at ??
                leave.toDate ??
                leave.to;
            if (!rawDate) return;
            const d = typeof rawDate === "number" ? new Date(rawDate) : new Date(rawDate);
            if (Number.isNaN(d.getTime())) return;
            const key = getMonthKey(d);
            if (baseCounts[key] === undefined) return;

            const rawType = leave.type || "OTHER";
            const typeKey = TYPE_LABELS[rawType] ? rawType : "OTHER";
            if (!countsByType[typeKey]) {
                countsByType[typeKey] = { ...baseCounts };
            }
            countsByType[typeKey][key] += 1;
        });

        const builtSeries = Object.entries(countsByType)
            .map(([type, tally]) => ({
                type,
                label: TYPE_LABELS[type] || "Autre",
                color: TYPE_COLORS[type] || TYPE_COLORS.OTHER,
                values: monthKeys.map((key) => tally[key]),
            }))
            .filter((entry) => entry.values.some((value) => value > 0));

        const allValues = builtSeries.flatMap((entry) => entry.values);
        const max = Math.max(1, ...allValues);
        const sum = allValues.reduce((acc, value) => acc + value, 0);

        return {
            months: monthLabels,
            series: builtSeries,
            maxCount: max,
            total: sum,
        };
    }, [leaves]);

    const chartPoints = useMemo(() => {
        const usableHeight = chartHeight - chartTop - chartBottom;
        const usableWidth = chartWidth - chartLeft;
        const step = months.length > 1 ? usableWidth / (months.length - 1) : usableWidth;
        return series.map((entry) =>
            entry.values.map((value, index) => {
                const x = chartLeft + index * step;
                const y = chartTop + (1 - value / maxCount) * usableHeight;
                return { x, y, value };
            })
        );
    }, [months.length, series, maxCount, chartHeight, chartTop, chartBottom, chartWidth, chartLeft]);

    const yTicks = useMemo(() => {
        const max = Math.max(1, maxCount);
        const mid = Math.round(max / 2);
        return Array.from(new Set([0, mid, max]));
    }, [maxCount]);

    const xPositions = useMemo(() => {
        const points = chartPoints[0] || [];
        return points.map((point) => point.x);
    }, [chartPoints]);

    const hasData = total > 0;

    return (
        <Card
            sx={{
                borderRadius: 4,
                height: "100%",
                boxShadow: "0 22px 44px rgba(15, 23, 42, 0.14)",
                border: "1px solid rgba(148, 163, 184, 0.28)",
                background:
                    "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 248, 255, 0.92) 50%, rgba(255, 255, 255, 0.94) 100%)",
                overflow: "hidden",
                position: "relative",
                backdropFilter: "blur(12px)",
                transition: "transform 200ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1), border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 30px 68px rgba(15, 23, 42, 0.18)",
                    borderColor: "rgba(148, 163, 184, 0.45)",
                },
            }}
        >
            {/* Accent top bar */}
            <Box
                sx={{
                    height: 5,
                    background: "linear-gradient(90deg, #FF9015, rgba(255, 193, 0, 0.85), #FF9015)",
                }}
            />
            {/* Radial glow overlay */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: `
                        radial-gradient(circle at 12% 12%, rgba(255, 144, 21, 0.07), transparent 50%),
                        radial-gradient(circle at 88% 12%, rgba(15, 23, 42, 0.04), transparent 45%),
                        radial-gradient(circle at 50% 100%, rgba(15, 23, 42, 0.03), transparent 55%)
                    `,
                    pointerEvents: "none",
                }}
            />
            <CardContent sx={{ height: "100%", p: { xs: 2, md: 3 }, position: "relative" }}>
                <Box>
                    <Stack spacing={compact ? 0.5 : 1}>
                    <Typography
                        variant={compact ? "subtitle1" : "h6"}
                        sx={{
                            fontWeight: 700,
                            letterSpacing: "-0.02em",
                            color: "#1e293b",
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "rgba(107, 114, 128, 0.85)",
                            fontWeight: 400,
                            letterSpacing: "0.01em",
                        }}
                    >
                        {subtitle}
                    </Typography>
                    </Stack>
                </Box>

                <Box
                    sx={{
                        mt: compact ? 1.5 : 2.5,
                        position: "relative",
                        width: "100%",
                        p: 1.5,
                        borderRadius: 3,
                        background: "rgba(248, 250, 252, 0.6)",
                        border: "1px solid rgba(148, 163, 184, 0.12)",
                    }}
                >
                    <Box
                        component="svg"
                        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                        sx={{
                            width: "100%",
                            height: compact ? 140 : 150,
                            display: "block",
                        }}
                    >
                        <ChartAxes
                            width={chartWidth}
                            height={chartHeight}
                            left={chartLeft}
                            top={chartTop}
                            bottom={chartBottom}
                            yTicks={yTicks}
                            xLabels={months}
                            xPositions={xPositions}
                            compact={compact}
                        />

                        {series.map((entry, entryIndex) => {
                            const points = chartPoints[entryIndex] || [];
                            const linePoints = points.map((p) => `${p.x},${p.y}`).join(" ");
                            return (
                                <g key={entry.type}>
                                    <polyline
                                        fill="none"
                                        stroke={entry.color}
                                        strokeWidth="2.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        points={linePoints}
                                        opacity={0.9}
                                    />
                                    {points.map((point, index) => (
                                        <Tooltip
                                            key={`${entry.type}-${months[index]}`}
                                            title={`${entry.label} • ${months[index]} : ${point.value}`}
                                            arrow
                                            slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        bgcolor: "rgba(15, 23, 42, 0.92)",
                                                        backdropFilter: "blur(8px)",
                                                        borderRadius: "8px",
                                                        fontSize: "0.75rem",
                                                        fontWeight: 500,
                                                        px: 1.5,
                                                        py: 0.75,
                                                        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.25)",
                                                    },
                                                },
                                                arrow: {
                                                    sx: {
                                                        color: "rgba(15, 23, 42, 0.92)",
                                                    },
                                                },
                                            }}
                                        >
                                            <circle
                                                cx={point.x}
                                                cy={point.y}
                                                r="2.8"
                                                fill={entry.color}
                                                stroke="rgba(255,255,255,0.9)"
                                                strokeWidth="1"
                                                style={{ cursor: "pointer" }}
                                            />
                                        </Tooltip>
                                    ))}
                                </g>
                            );
                        })}
                    </Box>
                    {!hasData && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: "rgba(148, 163, 184, 0.7)",
                                fontWeight: 500,
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                            }}
                        >
                            {emptyLabel}
                        </Typography>
                    )}
                </Box>

                <Box>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{ mt: compact ? 1.5 : 2.5, flexWrap: "wrap" }}
                    >
                        {series.map((entry) => (
                            <Stack
                                key={entry.type}
                                direction="row"
                                spacing={0.75}
                                alignItems="center"
                                sx={{
                                    px: 1.25,
                                    py: 0.5,
                                    borderRadius: 2,
                                    background: "rgba(248, 250, 252, 0.7)",
                                    border: "1px solid rgba(148, 163, 184, 0.12)",
                                    transition: "background 200ms ease, border-color 200ms ease",
                                    "&:hover": {
                                        background: "rgba(248, 250, 252, 1)",
                                        borderColor: "rgba(148, 163, 184, 0.28)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        backgroundColor: entry.color,
                                        boxShadow: `0 0 6px ${entry.color}55`,
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "rgba(71, 85, 105, 0.9)",
                                        fontWeight: 500,
                                        letterSpacing: "0.01em",
                                        fontSize: "0.7rem",
                                    }}
                                >
                                    {entry.label}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Box>

                {!compact && (
                    <Box sx={{ px: 1, mt: 2.5 }}>
                        <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
                            {months.map((label, index) => (
                                <Box
                                    key={label}
                                    sx={{
                                        minWidth: 56,
                                        px: 1.25,
                                        py: 0.75,
                                        borderRadius: 2,
                                        transition: "background 200ms ease",
                                        "&:hover": {
                                            background: "rgba(255, 144, 21, 0.06)",
                                        },
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "rgba(107, 114, 128, 0.75)",
                                            fontWeight: 500,
                                            letterSpacing: "0.01em",
                                        }}
                                    >
                                        {label}
                                    </Typography>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 700,
                                            color: "#1e293b",
                                            letterSpacing: "-0.01em",
                                        }}
                                    >
                                        {series.reduce((acc, entry) => acc + (entry.values[index] || 0), 0)}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}
